/**
 * Data slice actions — orchestrates all Python backend analysis computations.
 *
 * Each analysis area follows a calculate/get pattern:
 *   - calculate*() triggers the Python backend to compute and persist results (Parquet files)
 *   - get*Data() reads previously computed results and stores them in Redux
 *
 * Higher-level orchestrators (calculateAll, runAnalysis, retrieveAll) coordinate
 * multiple analysis steps in sequence.
 */
import { backendPy, fetchAnalysisStatus } from "System/system";
import store from "System/reduxStore";
// -------------------------------------------------------
import { calculateInvoiceData } from "Redux/invoice/actions";
import { calculatePerformanceData } from "Redux/performance/actions";
import { calculateAdvanceData } from "Redux/advance/actions";
import { getCriteriaData } from "Redux/criteria/actions";
import { getInvoiceData } from "Redux/invoice/actions";
import { getPerformanceData } from "Redux/performance/actions";
import { getAdvanceData } from "Redux/advance/actions";
import { getDashboardData } from "Redux/dashboard/actions";


// -------------------------------------------------------
// Returns the computation engine mode: "numba" (JIT-compiled, faster) or "pandas" (default)
const getMode = () => store.getState().data.useNumba ? "numba" : "pandas";

// -------------------------------------------------------
// SCENARIOS
//
// A scenario is a MODIFIED input set whose results live separately from the
// original, so the two can be compared. The mechanic: a scenario is a derived
// projectid. Every backend module resolves paths via data_path(projectid, file),
// so running with `{id}__scn_{sid}` writes into an isolated sibling directory
// with no changes to any analysis module.
//
// scenarioId "base" === the original project.
export const BASE = "base";
const SCN_SEP = "__scn_";

/** Resolve the projectid to send to the backend for a given scenario. */
export const resolvePid = (id, scenarioId = BASE) =>
    !scenarioId || scenarioId === BASE ? id : `${id}${SCN_SEP}${scenarioId}`;

/** The project id + datapath for a scenario, ready to spread into a backendPy body. */
const target = (scenarioId = BASE) => {
    const { id, datapath } = store.getState().project;
    return { projectid: resolvePid(id, scenarioId), datapath };
};

// has* flags: for the BASE project these live in the project slice and behave
// exactly as before (including nulling savedReport). Scenario flags are routed to
// the data slice instead (keyed by the same "hasX" name), so a scenario run can
// never clobber the base project's flags or wipe its generated executive report.
const HAS_ACTION = {
    hasAging: "SET_HAS_AGING",
    hasRollforward: "SET_HAS_ROLLFORWARD",
    hasDelinquencies: "SET_HAS_DELINQUENCIES",
    hasDilution: "SET_HAS_DILUTION",
    hasLoss: "SET_HAS_LOSS",
    hasYield: "SET_HAS_YIELD",
    hasAggregateReserve: "SET_HAS_AGGREGATE_RESERVE",
    hasBuyerParent: "SET_HAS_BUYER_PARENT",
    hasConcentrationLimits: "SET_HAS_CONCENTRATION_LIMITS",
    hasNerb: "SET_HAS_NERB",
};
const setHas = (dispatch, scenarioId, flag) => {
    if (!scenarioId || scenarioId === BASE) dispatch({ type: HAS_ACTION[flag] });
    else dispatch({ type: "SET_SCENARIO_FLAG", scenarioId, flag });
};

// -------------------------------------------------------
// Switch the active scenario (drives what every analysis screen shows). For a
// scenario, hydrate its has* flags from the backend first — after a reload the
// data slice is empty, so without this the screens wouldn't know to fetch.
export const activateScenario = (scenarioId = BASE) => async (dispatch) => {
    if (scenarioId && scenarioId !== BASE) {
        const { id } = store.getState().project;
        try {
            const status = await fetchAnalysisStatus(resolvePid(id, scenarioId));
            dispatch({ type: "SET_SCENARIO_STATUS", scenarioId, status: status || {} });
        } catch (e) {
            // fall through — the screens will just show "not computed"
        }
    }
    dispatch({ type: "SET_ACTIVE_SCENARIO", scenarioId });
};

// -------------------------------------------------------
// Delete a scenario: remove its data directory, its persisted metadata, and its
// in-memory results. If it was active, fall back to the original.
export const deleteScenario = (scenarioId) => async (dispatch) => {
    const { id } = store.getState().project;
    try {
        await backendPy("scenario", "delete", { projectid: id, scenarioId });
    } catch (e) {
        // best-effort; still clear client state
    }
    if (store.getState().data.activeScenario === scenarioId) {
        dispatch({ type: "SET_ACTIVE_SCENARIO", scenarioId: BASE });
    }
    dispatch({ type: "REMOVE_SCENARIO", id: scenarioId });
    dispatch({ type: "CLEAR_SCENARIO_RESULTS", scenarioId });
};

// -------------------------------------------------------
// Flattens normMap into the prefixed dict the backend expects:
//   { "pINV": "Sales", "nCRD": "Dilution", ... }
// ("p" = positive/debit amount, "n" = negative/credit.)
const flattenNormMap = (normMap) => {
    const norm = {};
    if (normMap) {
        Object.entries(normMap).forEach(([docType, mapping]) => {
            if (mapping.positive) norm["p" + docType] = mapping.positive;
            if (mapping.negative) norm["n" + docType] = mapping.negative;
        });
    }
    return JSON.stringify(norm);
};

// -------------------------------------------------------
// -------------------------------------------------------
// Fetches default buyer credit ratings from the backend for concentration limit analysis
export const loadDefaultRatings = (cb) => async (dispatch) => {
    const { id, datapath } = store.getState().project;
    const result = await backendPy("concentration_limits", "get_default_ratings", {
        projectid: id,
        datapath,
    });
    if (result.data && result.data.buyerRatings) {
        dispatch({ type: "SET_BUYER_RATINGS", buyerRatings: result.data.buyerRatings });
    }
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
/** Generate an executive report from all available analysis data using LLM.
 *  Saves the report + a timestamp to the project so it persists across sessions. */
export const generateExecutiveReport = (cb) => async (dispatch) => {
    const { id, datapath } = store.getState().project;
    const result = await backendPy("report", "generate", {
        projectid: id,
        datapath,
    });
    if (result.data) {
        dispatch({ type: "SET_REPORT_DATA", report: result.data });
        dispatch({
            type: "SET_PROJECT_REPORT",
            savedReport: {
                data: result.data,
                generatedAt: new Date().toISOString(),
            },
        });
    }
    cb(result.data);
};

/** Load a previously saved report from the project state into the data store. */
export const loadSavedReport = () => (dispatch) => {
    const { savedReport } = store.getState().project;
    if (savedReport?.data) {
        dispatch({ type: "SET_REPORT_DATA", report: savedReport.data });
    }
};

// -------------------------------------------------------
// -------------------------------------------------------
// Uses AI/ML classification to suggest column-to-canonical mappings based on column names and sample data
export const suggestMapping = (columns, canonicals, samples, cb) => async (dispatch) => {
    const result = await backendPy("classification", "suggest_mapping", {
        columns: JSON.stringify(columns),
        canonicals: JSON.stringify(canonicals),
        samples: JSON.stringify(samples),
    });
    cb(result.data);
};

// -------------------------------------------------------
// -------------------------------------------------------
// Classifies a buyer/company name into a credit rating using the project's rating scale
export const classifyBuyer = (companyName, cb) => async (dispatch) => {
    const { ratingScale } = store.getState().project;
    const result = await backendPy("classification", "classify", {
        company_name: companyName,
        rating_scale: JSON.stringify(ratingScale || []),
    });
    cb(result.data);
};

// -------------------------------------------------------
// -------------------------------------------------------
// Calculates NERB (Net Eligible Receivable Balance) using buyer ratings and rating scale
export const calculateNerb = (cb, scenarioId = BASE) => async (dispatch) => {
    const { ratingScale, buyerRatings } = store.getState().project;
    await backendPy("nerb", "calculate", {
        ...target(scenarioId),
        ratingScaleString: JSON.stringify(ratingScale || []),
        buyerRatingsString: JSON.stringify(buyerRatings || {}),
    });
    setHas(dispatch, scenarioId, "hasNerb");
    await dispatch(getNerbData(() => {}, scenarioId));
    cb();
};
// -------------------------------------------------------
// -------------------------------------------------------
export const getNerbData = (cb, scenarioId = BASE) => async (dispatch) => {
    const result = await backendPy("nerb", "read", target(scenarioId));
    if (result.data) {
        dispatch({ type: "SET_RESULT", key: "nerb", value: result.data, scenarioId });
    }
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
// runAll — the single canonical full-analysis orchestrator.
//
// Used by BOTH the Parameters "Run Analysis" button and an agent-triggered
// scenario re-run. Pass a scenarioId to run the whole pipeline against a
// modified input set; "base" runs the original project.
//
// Step order encodes the real dependency graph. The previous calculateAll ran
// only 7 steps and silently SKIPPED loss, yield and aggregateReserve, while the
// progress modal ticked steps that never executed.
//
//   aging               -> aging.parquet.gzip + aging_gross.parquet.gzip
//   buyerParent         -> buyer_parent.parquet.gzip
//   concentrationLimits -> needs buyer_parent
//   nerb                -> nerb.parquet.gzip
//   yieldReserve        -> needs aging
//   lossReserve         -> needs aging_gross + nerb
//   aggregateReserve    -> needs loss + dilution + yield
//
// Runs with strict=1, so a missing upstream RAISES instead of silently
// substituting placeholder values (see receivables_loss._load_nerb_series).
export const runAll = (scenarioId = BASE, onStep, onDone, onError) => async (dispatch) => {
    const prevMode = store.getState().data.useNumba;
    dispatch({ type: "SET_NUMBA", value: true });

    const { projectType, parameters, buyerRatings } = store.getState().project;
    const referenceDate = parameters?.referenceDate || "";
    const d = () => {};
    const s = scenarioId;

    const receivables = [
        ["aging", () => dispatch(calculateAging(d, s))],
        ["rollforward", () => dispatch(calculateRollforward(d, s))],
        ["delinquencies", () => dispatch(calculateDelinquencies(d, s))],
        ["dilution", () => dispatch(calculateDilution(d, s))],
        ["buyerParent", () => dispatch(calculateBuyerParent(referenceDate, d, s))],
        ["concentrationLimits", () => dispatch(calculateConcentrationLimits(referenceDate, d, true, s))],
        ["nerb", () => Object.keys(buyerRatings || {}).length > 0
            ? dispatch(calculateNerb(d, s))
            : Promise.resolve()],
        ["yieldReserve", () => dispatch(calculateYield(d, s))],
        ["lossReserve", () => dispatch(calculateLoss(d, s))],
        ["aggregateReserve", () => dispatch(calculateAggregateReserve(d, s))],
    ];

    const payables = [
        ["aging", () => dispatch(calculateAging(d, s))],
        ["rollforward", () => dispatch(calculateRollforward(d, s))],
        ["dashboard", () => dispatch(calculatePayablesDashboard(d))],
        ["supplier", () => dispatch(calculateSupplier(d))],
        ["invoice", () => dispatch(calculateInvoiceData(d))],
        ["performance", () => dispatch(calculatePerformanceData(d))],
        ["advance", () => dispatch(calculateAdvanceData(d))],
        ["criteria", () => dispatch(calculateCriteria(d))],
    ];

    const steps = projectType === "payables" ? payables : receivables;

    try {
        for (let i = 0; i < steps.length; i++) {
            onStep?.(steps[i][0], i, steps.length);
            await steps[i][1]();
        }
        onDone?.();
    } catch (e) {
        // Previously a mid-pipeline 500 left the progress modal spinning forever.
        const msg = e?.response?.data?.error || e?.message || String(e);
        onError?.(msg);
        throw e;
    } finally {
        dispatch({ type: "SET_NUMBA", value: prevMode });
    }
};

// Payables dashboard calculation
export const calculatePayablesDashboard = (cb) => async (dispatch) => {
    const { id, datapath, parameters } = store.getState().project;
    await backendPy("dashboard", "calculate", {
        projectid: id,
        datapath,
        reference_date: parameters?.referenceDate || "",
    });
    await dispatch(getDashboardData(() => {}));
    cb();
};

// Payables criteria calculation
export const calculateCriteria = (cb) => async (dispatch) => {
    const { id, datapath } = store.getState().project;
    await backendPy("criteria", "calculate", {
        projectid: id,
        datapath,
    });
    await dispatch(getCriteriaData(() => {}));
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
// Reads the merged/filtered master dataset from the Python backend
export const getMasterData = (cb) => async (dispatch) => {
    const { id, datapath } = store.getState().project;
    const result = await backendPy("master", "read", {
        projectid: id,
        datapath,
    });
    if (result.data) {
        dispatch({ type: "SET_MASTER_DATA", master: result.data });
    }
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
// Reads rollover discrepancy data (mismatches between reporting periods)
export const getRolloverDiscrepancies = (cb) => async (dispatch) => {
    const { id, datapath } = store.getState().project;
    const result = await backendPy("rollover", "read_discrepancies", {
        projectid: id,
        datapath,
    });
    console.log("getRolloverDiscrepancies", result.data);
    if (result.data) {
        dispatch({
            type: "SET_ROLLOVER_DISCREPANCIES",
            discrepancies: result.data,
        });
    }
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
export const calculateAging = (cb, scenarioId = BASE) => async (dispatch) => {
    await backendPy("aging", "calculate", {
        ...target(scenarioId),
        mode: getMode(),
    });
    setHas(dispatch, scenarioId, "hasAging");
    await dispatch(getAgingData(() => {}, scenarioId));
    cb();
};
// -------------------------------------------------------
// -------------------------------------------------------
export const getAgingData = (cb, scenarioId = BASE) => async (dispatch) => {
    const result = await backendPy("aging", "read", target(scenarioId));
    if (result.data) {
        dispatch({ type: "SET_RESULT", key: "aging", value: result.data, scenarioId });
    }
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
// Scrub: static record-level integrity check over the raw master pool.
// Passes normMap so the backend can flag document types with no rollforward
// category mapping (which rollforward would otherwise silently bucket).
export const calculateScrub = (cb = () => {}) => async (dispatch) => {
    const { id, datapath, normMap } = store.getState().project;
    await backendPy("scrub", "calculate", {
        projectid: id,
        datapath,
        normString: JSON.stringify(normMap || {}),
    });
    await dispatch(getScrubData(() => {}));
    cb();
};
// -------------------------------------------------------
// -------------------------------------------------------
export const getScrubData = (cb = () => {}) => async (dispatch) => {
    const { id, datapath } = store.getState().project;
    const result = await backendPy("scrub", "read", {
        projectid: id,
        datapath,
    });
    if (result.data) {
        dispatch({ type: "SET_SCRUB_DATA", scrub: result.data });
    }
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
// Basic fraud detection: Benford's Law, duplicate invoices, round amounts.
export const detectFraud = (cb = () => {}) => async (dispatch) => {
    const { id, datapath } = store.getState().project;
    const result = await backendPy("fraud", "detect", {
        projectid: id,
        datapath,
    });
    if (result.data) {
        dispatch({ type: "SET_FRAUD_DATA", fraud: result.data });
    }
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
// Rollforward Analysis: inspect the computed rollforward and explain every day
// that does not reconcile (Discrepancy > tolerance), attributing the gap to the
// records that closed on that day.
export const analyzeRollforward = (cb = () => {}) => async (dispatch) => {
    const { id, datapath } = store.getState().project;
    const result = await backendPy("rollforward", "analyze", {
        projectid: id,
        datapath,
    });
    if (result.data) {
        dispatch({ type: "SET_ROLLFORWARD_ANALYSIS", rollforwardAnalysis: result.data });
    }
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
// Calculates rollforward (daily activity reconciliation: Sales/Collection/Dilution categories).
// normMap translates document types into positive ("p" prefix) and negative ("n" prefix) activity categories.
export const calculateRollforward = (cb, scenarioId = BASE) => async (dispatch) => {
    const { normMap } = store.getState().project;
    await backendPy("rollforward", "calculate", {
        ...target(scenarioId),
        normString: flattenNormMap(normMap),
        mode: getMode(),
    });
    setHas(dispatch, scenarioId, "hasRollforward");
    await dispatch(getRollforwardData(() => {}, scenarioId));
    cb();
};
// -------------------------------------------------------
// -------------------------------------------------------
export const getRollforwardData = (cb, scenarioId = BASE) => async (dispatch) => {
    const result = await backendPy("rollforward", "read", target(scenarioId));
    if (result.data) {
        dispatch({ type: "SET_RESULT", key: "rollforward", value: result.data, scenarioId });
    }
    cb();
};


// -------------------------------------------------------
// -------------------------------------------------------
// Calculates receivables delinquency metrics (overdue payment analysis)
export const calculateDelinquencies = (cb, scenarioId = BASE) => async (dispatch) => {
    const { normMap, calcParams } = store.getState().project;
    await backendPy("receivables_delinquencies", "calculate", {
        ...target(scenarioId),
        normString: flattenNormMap(normMap),
        mode: getMode(),
        calcParamsString: JSON.stringify(calcParams?.delinquencies || {}),
    });
    setHas(dispatch, scenarioId, "hasDelinquencies");
    await dispatch(getDelinquenciesData(() => {}, scenarioId));
    cb();
};
// -------------------------------------------------------
// -------------------------------------------------------
export const getDelinquenciesData = (cb, scenarioId = BASE) => async (dispatch) => {
    const result = await backendPy("receivables_delinquencies", "read", target(scenarioId));
    if (result.data) {
        dispatch({ type: "SET_RESULT", key: "delinquencies", value: result.data, scenarioId });
    }
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
// Calculates receivables dilution metrics (credit notes, returns, adjustments as % of sales)
export const calculateDilution = (cb, scenarioId = BASE) => async (dispatch) => {
    const { normMap, calcParams } = store.getState().project;
    await backendPy("receivables_dilution", "calculate", {
        ...target(scenarioId),
        normString: flattenNormMap(normMap),
        mode: getMode(),
        calcParamsString: JSON.stringify(calcParams?.dilution || {}),
    });
    setHas(dispatch, scenarioId, "hasDilution");
    await dispatch(getDilutionData(() => {}, scenarioId));
    cb();
};
// -------------------------------------------------------
// -------------------------------------------------------
export const getDilutionData = (cb, scenarioId = BASE) => async (dispatch) => {
    const result = await backendPy("receivables_dilution", "read", target(scenarioId));
    if (result.data) {
        dispatch({ type: "SET_RESULT", key: "dilution", value: result.data, scenarioId });
    }
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
// Calculates receivables loss reserve metrics (forward-window default ratio inputs)
export const calculateLoss = (cb, scenarioId = BASE) => async (dispatch) => {
    const { normMap, calcParams } = store.getState().project;
    await backendPy("receivables_loss", "calculate", {
        ...target(scenarioId),
        normString: flattenNormMap(normMap),
        mode: getMode(),
        calcParamsString: JSON.stringify(calcParams?.lossReserve || {}),
        // Refuse to fall back to a hardcoded placeholder NERB / zero aging proxy —
        // that would silently produce fabricated reserve numbers.
        strict: "1",
    });
    setHas(dispatch, scenarioId, "hasLoss");
    await dispatch(getLossData(() => {}, scenarioId));
    cb();
};
// -------------------------------------------------------
// -------------------------------------------------------
export const getLossData = (cb, scenarioId = BASE) => async (dispatch) => {
    const result = await backendPy("receivables_loss", "read", target(scenarioId));
    if (result.data) {
        dispatch({ type: "SET_RESULT", key: "loss", value: result.data, scenarioId });
    }
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
// Calculates the receivables yield & fee reserve (mirrors Yield&Fee_Res_sales-based sheet)
export const calculateYield = (cb, scenarioId = BASE) => async (dispatch) => {
    const { normMap, calcParams } = store.getState().project;
    await backendPy("receivables_yield", "calculate", {
        ...target(scenarioId),
        normString: flattenNormMap(normMap),
        mode: getMode(),
        calcParamsString: JSON.stringify(calcParams?.yieldReserve || {}),
        strict: "1", // missing aging must raise, not silently return zeros
    });
    setHas(dispatch, scenarioId, "hasYield");
    await dispatch(getYieldData(() => {}, scenarioId));
    cb();
};
// -------------------------------------------------------
// -------------------------------------------------------
export const getYieldData = (cb, scenarioId = BASE) => async (dispatch) => {
    const result = await backendPy("receivables_yield", "read", target(scenarioId));
    if (result.data) {
        dispatch({ type: "SET_RESULT", key: "yield", value: result.data, scenarioId });
    }
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
// Combines loss + dilution reserves into the aggregate fungible structural test
// (per Loss Reserve guidance A119–A129; mirrors Key Perf Ratios sheet col W).
export const calculateAggregateReserve = (cb, scenarioId = BASE) => async (dispatch) => {
    await backendPy("aggregate_reserve", "calculate", target(scenarioId));
    setHas(dispatch, scenarioId, "hasAggregateReserve");
    await dispatch(getAggregateReserveData(() => {}, scenarioId));
    cb();
};
// -------------------------------------------------------
// -------------------------------------------------------
export const getAggregateReserveData = (cb, scenarioId = BASE) => async (dispatch) => {
    const result = await backendPy("aggregate_reserve", "read", target(scenarioId));
    if (result.data) {
        dispatch({ type: "SET_RESULT", key: "aggregateReserve", value: result.data, scenarioId });
    }
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
// Calculates buyer/parent-level accounts receivable aggregation at a given reference date
export const calculateBuyerParent = (referenceDate, cb, scenarioId = BASE) => async (dispatch) => {
    const { calcParams } = store.getState().project;
    await backendPy("buyer_parent", "calculate", {
        ...target(scenarioId),
        reference_date: referenceDate,
        mode: getMode(),
        calcParamsString: JSON.stringify(calcParams?.buyerParent || {}),
    });
    setHas(dispatch, scenarioId, "hasBuyerParent");
    await dispatch(getBuyerParentData(() => {}, scenarioId));
    cb();
};
// -------------------------------------------------------
// -------------------------------------------------------
export const getBuyerAr = (cb) => async (dispatch) => {
    const { id } = store.getState().project;
    const result = await backendPy("master", "get_buyer_ar", { projectid: id });
    if (result.data) {
        dispatch({ type: "SET_BUYER_AR", buyerAr: result.data.buyers });
    }
    if (cb) cb();
};

export const getBuyerParentData = (cb, scenarioId = BASE) => async (dispatch) => {
    const result = await backendPy("buyer_parent", "read", target(scenarioId));
    if (result.data) {
        dispatch({ type: "SET_RESULT", key: "buyerParent", value: result.data, scenarioId });
    }
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
// Calculates concentration limits (max exposure per buyer based on credit ratings).
// Depends on buyerParent — will compute it first unless skipBuyerParent is true (e.g., in calculateAll).
export const calculateConcentrationLimits = (referenceDate, cb, skipBuyerParent = false, scenarioId = BASE) => async (dispatch) => {
    const { ratingScale, buyerRatings, calcParams } = store.getState().project;
    if (!skipBuyerParent) {
        await dispatch(calculateBuyerParent(referenceDate, () => {}, scenarioId));
    }
    await backendPy("concentration_limits", "calculate", {
        ...target(scenarioId),
        mode: getMode(),
        ratingScaleString: JSON.stringify(ratingScale || []),
        buyerRatingsString: JSON.stringify(buyerRatings || {}),
        calcParamsString: JSON.stringify(calcParams?.concentrationLimits || {}),
    });
    setHas(dispatch, scenarioId, "hasConcentrationLimits");
    await dispatch(getConcentrationLimitsData(() => {}, scenarioId));
    cb();
};
// -------------------------------------------------------
// -------------------------------------------------------
export const getConcentrationLimitsData = (cb, scenarioId = BASE) => async (dispatch) => {
    const result = await backendPy("concentration_limits", "read", target(scenarioId));
    if (result.data) {
        dispatch({ type: "SET_RESULT", key: "concentrationLimits", value: result.data, scenarioId });
    }
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
// Flattens the nested parameters structure into a flat dict keyed by parameter id.
// Used to send all analysis parameters (supplier eligibility, invoice eligibility, etc.) to the Python backend.
export const getLinearParameters = () => {
    const { parameters } = store.getState().project;
    const linearParameters = {};
    linearParameters.reference_date = parameters.referenceDate;
    parameters.linear.forEach(
        (area) => {
            area.entries.forEach((item) => {
                linearParameters[item.id] = {
                    ...item,
                    area: area.area,
                };
            });
        }
    );
    return linearParameters;
}

// -------------------------------------------------------
// -------------------------------------------------------
export const calculateSupplier = (cb) => async (dispatch) => {
    const { id, datapath, calcParams, parameters, normMap } = store.getState().project;
    const referenceDate = parameters?.referenceDate || "";
    const norm = {};
    if (normMap) {
        Object.entries(normMap).forEach(([docType, mapping]) => {
            if (mapping.positive) norm["p" + docType] = mapping.positive;
            if (mapping.negative) norm["n" + docType] = mapping.negative;
        });
    }
    const params = { ...(calcParams?.supplierEligibility || {}), ...(calcParams?.performanceRatios || {}) };
    await backendPy("supplier", "calculate", {
        projectid: id,
        datapath,
        reference_date: referenceDate,
        calcParamsString: JSON.stringify(params),
        normString: JSON.stringify(norm),
        mode: getMode(),
    });
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
// Generic calculate action — calls any Python rubric's "calculate" endpoint with linear parameters
export const calculateAny = (rubric, cb) => async (dispatch) => {
    const { id, datapath } = store.getState().project;
    await backendPy(rubric, "calculate", {
        projectid: id,
        datapath,
        linearParametersString: JSON.stringify(getLinearParameters()),
    });
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
export const getSupplierData = (cb) => async (dispatch) => {
    const { id, datapath } = store.getState().project;
    const result = await backendPy("supplier", "read", {
        projectid: id,
        datapath,
    });
    if (result.data) {
        dispatch({ type: "SET_SUPPLIER_DATA", supplier: result.data });
    }
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
// Loads all previously computed analysis results into Redux (used when returning to a project with existing analysis)
export const retrieveAll = (cb) => async (dispatch) => {
    const dummy = () => { };
    await dispatch(getAgingData(dummy));
    await dispatch(getRollforwardData(dummy));
    await dispatch(getCriteriaData(dummy));
    await dispatch(getSupplierData(dummy));
    await dispatch(getInvoiceData(dummy));
    await dispatch(getPerformanceData(dummy));
    await dispatch(getAdvanceData(dummy));
    await dispatch(getDashboardData(dummy));
    cb();
}



// -------------------------------------------------------
// -------------------------------------------------------
// Runs the second-phase analysis pipeline: supplier metrics -> invoice eligibility -> performance ratios -> advance rates
export const runAnalysis = (cb) => async (dispatch) => {
    const dummy = () => { };
    console.log("runAnalysis");
    await dispatch(calculateSupplier(dummy));
    console.log("runAnalysis supplier");
    await dispatch(calculateInvoiceData(dummy));
    console.log("runAnalysis invoice");
    await dispatch(calculatePerformanceData(dummy));
    console.log("runAnalysis performance");
    await dispatch(calculateAdvanceData(dummy));
    console.log("runAnalysis advance");
    cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
export const generateReport = () => async (dispatch) => {
    const { id, datapath } = store.getState().project;
    await backendPy("report", "generate", {
        projectid: id,
        datapath,
    });
};

// -------------------------------------------------------
// -------------------------------------------------------
// Fetches the min/max date range from the filtered master dataset
export const getDateRange = (cb) => async (dispatch) => {
    const { id, datapath, parameters } = store.getState().project;
    const result = await backendPy("master", "get_date_range", {
        projectid: id,
        datapath,
    });
    if (result.data) {
        dispatch({ type: "SET_DATE_RANGE", dateRange: result.data });
        // Default the reference date to the latest date in the dataset
        if (!parameters?.referenceDate) {
            dispatch({
                type: "SET_REFERENCE_DATE",
                referenceDate: result.data.max_date,
            });
        }
    }
    if (cb) cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
// Fetches positive/negative amount totals per document type from filtered master
export const getDocTypeSums = (cb) => async (dispatch) => {
    const { id, datapath } = store.getState().project;
    const result = await backendPy("master", "get_doc_type_sums", {
        projectid: id,
        datapath,
    });
    if (result.data) {
        dispatch({ type: "SET_DOC_TYPE_SUMS", docTypeSums: result.data.doc_type_sums, docTypeDescriptions: result.data.doc_type_descriptions || [] });
    }
    if (cb) cb();
};

// -------------------------------------------------------
// -------------------------------------------------------
// Predicts document type categorizations using AI
export const predictCategories = (cb) => async (dispatch) => {
    const { docTypeSums, docTypeDescriptions } = store.getState().project;
    const { documentTypes } = store.getState().data;
    const docTypes = documentTypes?.document_types || [];
    const dealType = store.getState().project?.projectType || "receivables";
    const result = await backendPy("classification", "predict_categories", {
        deal_type: dealType,
        doc_types: JSON.stringify(docTypes),
        descriptions: JSON.stringify(docTypeDescriptions || []),
        sums: JSON.stringify(docTypeSums || []),
    });
    if (result.data?.predictedNormMap) {
        dispatch({ type: "SET_PREDICTED_NORM_MAP", predictedNormMap: result.data.predictedNormMap });
    }
    if (cb) cb(result.data);
};

// -------------------------------------------------------
// -------------------------------------------------------
// Fetches distinct document types (e.g., Invoice, Credit Note) from the filtered master dataset
export const getDocumentTypes = (cb) => async (dispatch) => {
    const { id, datapath } = store.getState().project;
    const result = await backendPy("master", "get_document_types", {
        projectid: id,
        datapath,
    });
    if (result.data) {
        dispatch({ type: "SET_DOCUMENT_TYPES", documentTypes: result.data });
    }
    cb();
};
